import React, {useEffect, useState} from "react";
import {Form, Table} from "react-bootstrap";
import {format} from "date-fns";
import CustomDatePicker from "common/components/DatePicker";
import ModalDialog from "common/components/Modal";
import {MATCH_STATUS_LABEL, roundName} from "./constants";

const DT_FORMAT = "yyyy-MM-dd HH:mm:ss";

export default function MatchManagement({api, eventId, rounds, teams, notify}) {
  const [roundCode, setRoundCode] = useState("R16");
  const [matches, setMatches] = useState([]);

  const [showCreate, setShowCreate] = useState(false);
  const [homeTeamId, setHomeTeamId] = useState("");
  const [awayTeamId, setAwayTeamId] = useState("");
  const [kickoffAt, setKickoffAt] = useState(new Date());
  const [displayOrder, setDisplayOrder] = useState(0);
  const [createConfirm, setCreateConfirm] = useState(false);

  const [settleTarget, setSettleTarget] = useState(null);
  const [settleConfirm, setSettleConfirm] = useState(false);
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [winnerTeamId, setWinnerTeamId] = useState("");

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editConfirm, setEditConfirm] = useState(false);
  const [editHomeTeamId, setEditHomeTeamId] = useState("");
  const [editAwayTeamId, setEditAwayTeamId] = useState("");
  const [editKickoffAt, setEditKickoffAt] = useState(new Date());
  const [editDisplayOrder, setEditDisplayOrder] = useState(0);

  const [statsTarget, setStatsTarget] = useState(null);
  const [statsData, setStatsData] = useState(null);

  const activeTeams = (teams ?? []).filter((t) => t.useYn !== "N");
  const roundOptions = (rounds ?? []).map((r) => ({
    code: r.roundCode,
    name: r.roundName ?? roundName(r.roundCode),
  }));
  const roundId = rounds?.find((r) => r.roundCode === roundCode)?.roundId;
  const selectedRoundName = roundOptions.find((r) => r.code === roundCode)?.name ?? roundName(roundCode);

  useEffect(() => {
    if (!roundOptions.length) return;
    const hasSelected = roundOptions.some((r) => r.code === roundCode);
    if (!hasSelected) {
      setRoundCode(roundOptions[0].code);
    }
  }, [roundOptions, roundCode]);

  function fetchMatches() {
    if (!eventId || !roundCode) return;
    api.getWorldcupMatches(eventId, roundCode).then((res) => {
      if (res?.result === "ok") {
        setMatches(res.matches ?? []);
      } else {
        setMatches([]);
      }
    });
  }

  useEffect(() => {
    fetchMatches();
  }, [eventId, roundCode]);

  if (!eventId) {
    return <p>이벤트를 먼저 선택해주세요.</p>;
  }

  function onCreateMatch() {
    if (!roundId || !homeTeamId || !awayTeamId || !kickoffAt) {
      alert("라운드, 팀, 경기 일시를 입력해주세요.");
      setCreateConfirm(false);
      return;
    }
    if (Number(homeTeamId) === Number(awayTeamId)) {
      alert("홈팀과 원정팀이 같을 수 없습니다.");
      setCreateConfirm(false);
      return;
    }
    api.createWorldcupMatch({
      eventId,
      roundId,
      homeTeamId: Number(homeTeamId),
      awayTeamId: Number(awayTeamId),
      kickoffAt: format(kickoffAt, DT_FORMAT),
      displayOrder: Number(displayOrder) || 0,
    }).then((res) => {
      if (res?.result === "ok") {
        notify("경기가 등록되었습니다.");
        setShowCreate(false);
        fetchMatches();
      } else {
        notify(res?.message ?? "실패하였습니다.", false);
      }
    }).finally(() => setCreateConfirm(false));
  }

  function onSettle() {
    if (!settleTarget || !winnerTeamId) {
      alert("승리 팀을 선택해주세요.");
      setSettleConfirm(false);
      return;
    }
    api.settleWorldcupMatch(settleTarget.matchId, {
      homeScore: Number(homeScore),
      awayScore: Number(awayScore),
      winnerTeamId: Number(winnerTeamId),
    }).then((res) => {
      if (res?.result === "ok") {
        notify(`정산 완료 (1장당 ${res.payoutPerTicket ?? 0}P)`);
        fetchMatches();
      } else {
        notify(res?.message ?? "실패하였습니다.", false);
      }
    }).finally(() => {
      setSettleConfirm(false);
      setSettleTarget(null);
    });
  }

  function onCancel() {
    if (!cancelTarget) return;
    api.cancelWorldcupMatch(cancelTarget.matchId).then((res) => {
      if (res?.result === "ok") {
        notify(`경기가 취소되었습니다. 환불 ${res.refundedCount ?? 0}건`);
        fetchMatches();
      } else {
        notify(res?.message ?? "실패하였습니다.", false);
      }
    }).finally(() => {
      setCancelConfirm(false);
      setCancelTarget(null);
    });
  }

  function openStats(match) {
    setStatsTarget(match);
    setStatsData(null);
    api.getWorldcupMatchStats(match.matchId).then((res) => {
      if (res?.result === "ok") {
        const stats = Array.isArray(res.stats) ? res.stats[0] : res.stats;
        setStatsData({
          ...res,
          stats: stats ?? {},
        });
      } else {
        setStatsTarget(null);
        notify(res?.message ?? "통계 조회 실패", false);
      }
    }).catch(() => {
      setStatsTarget(null);
      notify("통계 조회 실패", false);
    });
  }

  function closeStats() {
    setStatsTarget(null);
    setStatsData(null);
  }

  function openSettle(match) {
    setSettleTarget(match);
    setHomeScore(match.homeScore ?? 0);
    setAwayScore(match.awayScore ?? 0);
    setWinnerTeamId(match.homeTeamId);
    setSettleConfirm(true);
  }

  function parseKickoff(value) {
    if (!value) return new Date();
    const normalized = String(value).replace(" ", "T");
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  function openEdit(match) {
    setEditTarget(match);
    setEditHomeTeamId(String(match.homeTeamId ?? ""));
    setEditAwayTeamId(String(match.awayTeamId ?? ""));
    setEditKickoffAt(parseKickoff(match.kickoffAt));
    setEditDisplayOrder(match.displayOrder ?? 0);
    setEditConfirm(true);
  }

  function onEditMatch() {
    if (!editTarget) {
      setEditConfirm(false);
      return;
    }
    if (!editHomeTeamId || !editAwayTeamId || !editKickoffAt) {
      alert("팀과 경기 일시를 입력해주세요.");
      setEditConfirm(false);
      return;
    }
    if (Number(editHomeTeamId) === Number(editAwayTeamId)) {
      alert("홈팀과 원정팀이 같을 수 없습니다.");
      setEditConfirm(false);
      return;
    }
    api.updateWorldcupMatch({
      matchId: editTarget.matchId,
      homeTeamId: Number(editHomeTeamId),
      awayTeamId: Number(editAwayTeamId),
      kickoffAt: format(editKickoffAt, DT_FORMAT),
      displayOrder: Number(editDisplayOrder) || 0,
    }).then((res) => {
      if (res?.result === "ok") {
        notify("경기 정보가 수정되었습니다.");
        fetchMatches();
      } else {
        notify(res?.message ?? "실패하였습니다.", false);
      }
    }).finally(() => {
      setEditConfirm(false);
      setEditTarget(null);
    });
  }

  const editHasBets = editTarget
    ? (editTarget.homeTicketSum ?? 0) + (editTarget.awayTicketSum ?? 0) > 0
    : false;

  return (
    <article>
      <div className="flex" style={{marginBottom: 16, gap: 16, alignItems: "center"}}>
        <span style={{fontWeight: 700}}>라운드</span>
        <Form.Select style={{maxWidth: 200}} value={roundCode} onChange={(e) => setRoundCode(e.target.value)}>
          {roundOptions.map((r) => (
            <option key={r.code} value={r.code}>{r.name}</option>
          ))}
        </Form.Select>
        <button type="button" className="blue-btn" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? "등록 폼 닫기" : "경기 등록"}
        </button>
        <button type="button" className="grey-btn" onClick={fetchMatches}>새로고침</button>
      </div>

      {showCreate && (
        <div className="form-container event-form worldcup-form" style={{marginTop: 0}}>
          <div><span>* 홈팀</span></div>
          <div>
            <Form.Select value={homeTeamId} onChange={(e) => setHomeTeamId(e.target.value)}>
              <option value="">선택</option>
              {activeTeams.map((t) => (
                <option key={t.teamId} value={t.teamId}>{t.teamName} ({t.teamCode})</option>
              ))}
            </Form.Select>
          </div>

          <div><span>* 원정팀</span></div>
          <div>
            <Form.Select value={awayTeamId} onChange={(e) => setAwayTeamId(e.target.value)}>
              <option value="">선택</option>
              {activeTeams.map((t) => (
                <option key={t.teamId} value={t.teamId}>{t.teamName} ({t.teamCode})</option>
              ))}
            </Form.Select>
          </div>

          <div><span>* 경기 일시</span></div>
          <div>
            <CustomDatePicker placeholder="킥오프" value={kickoffAt} showTime onChange={setKickoffAt}/>
          </div>

          <div><span>정렬</span></div>
          <div>
            <input type="number" value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)}/>
          </div>
        </div>
      )}

      {showCreate && (
        <button type="button" className="green-btn" style={{marginBottom: 24}}
                onClick={() => setCreateConfirm(true)}>
          경기 등록
        </button>
      )}

      <Table bordered className="basic-table worldcup-match-table">
        <thead>
        <tr>
          <th>ID</th>
          <th>홈 vs 원정</th>
          <th>킥오프</th>
          <th>마감</th>
          <th>상태</th>
          <th>스코어</th>
          <th>풀(P)</th>
          <th>홈/원정 티켓</th>
          <th colSpan={4}>관리</th>
        </tr>
        </thead>
        <tbody>
        {matches?.length > 0 ? matches.map((match) => (
          <tr key={match.matchId}>
            <td>{match.matchId}</td>
            <td>{match.homeTeamName} vs {match.awayTeamName}</td>
            <td>{match.kickoffAt}</td>
            <td>{match.bettingCloseAt}</td>
            <td>{MATCH_STATUS_LABEL[match.status] ?? match.status}</td>
            <td>
              {match.homeScore != null ? `${match.homeScore} : ${match.awayScore}` : "VS"}
            </td>
            <td>{match.totalPoolPoint?.toLocaleString()}</td>
            <td>{match.homeTicketSum} / {match.awayTicketSum}</td>
            <td className="button-cell">
              <button type="button" className="green-btn"
                      disabled={match.settlementStatus === "SETTLED"
                        || match.status === "CANCELLED"
                        || match.status === "FINISHED"}
                      onClick={() => openEdit(match)}>
                수정
              </button>
            </td>
            <td className="button-cell">
              <button type="button" className="grey-btn"
                      disabled={match.settlementStatus === "SETTLED" || match.status === "CANCELLED"}
                      onClick={() => openSettle(match)}>
                정산
              </button>
            </td>
            <td className="button-cell">
              <button type="button" className="dark-btn"
                      disabled={match.settlementStatus === "SETTLED" || match.status === "CANCELLED"}
                      onClick={() => {
                        setCancelTarget(match);
                        setCancelConfirm(true);
                      }}>
                취소
              </button>
            </td>
            <td className="button-cell">
              <button type="button" className="blue-btn" onClick={() => openStats(match)}>
                통계
              </button>
            </td>
          </tr>
        )) : (
          <tr>
            <td colSpan={12}>{selectedRoundName} 경기가 없습니다.</td>
          </tr>
        )}
        </tbody>
      </Table>

      <ModalDialog show={createConfirm} onHide={() => setCreateConfirm(false)}
                   btnText="예" secondBtnText="아니요" hasTwoButtons
                   onOk={onCreateMatch} onClickSecond={() => setCreateConfirm(false)}>
        <p style={{textAlign: "center"}}>경기를 등록하시겠습니까?</p>
      </ModalDialog>

      <ModalDialog show={settleConfirm} onHide={() => setSettleConfirm(false)}
                   btnText="정산" secondBtnText="취소" hasTwoButtons
                   onOk={onSettle} onClickSecond={() => setSettleConfirm(false)}>
        <div className="worldcup-settle-form">
          <p style={{textAlign: "center", marginBottom: 16}}>
            {settleTarget?.homeTeamName} vs {settleTarget?.awayTeamName}
          </p>
          <div className="worldcup-settle-scores">
            <div className="worldcup-settle-score-field">
              <span className="worldcup-settle-score-label">홈 스코어</span>
              <input type="number" min={0} value={homeScore}
                     onChange={(e) => setHomeScore(e.target.value)}/>
            </div>
            <span className="worldcup-settle-score-divider" aria-hidden="true">:</span>
            <div className="worldcup-settle-score-field">
              <span className="worldcup-settle-score-label">원정 스코어</span>
              <input type="number" min={0} value={awayScore}
                     onChange={(e) => setAwayScore(e.target.value)}/>
            </div>
          </div>
          <div>
            <span style={{marginRight: 8}}>승리 팀</span>
            <Form.Select style={{maxWidth: 280, display: "inline-block"}}
                         value={winnerTeamId}
                         onChange={(e) => setWinnerTeamId(e.target.value)}>
              <option value={settleTarget?.homeTeamId}>{settleTarget?.homeTeamName} (홈)</option>
              <option value={settleTarget?.awayTeamId}>{settleTarget?.awayTeamName} (원정)</option>
            </Form.Select>
          </div>
          <p style={{marginTop: 12, fontSize: 14, color: "#666"}}>
            누적 풀: {settleTarget?.totalPoolPoint?.toLocaleString()}P
          </p>
        </div>
      </ModalDialog>

      <ModalDialog show={editConfirm} onHide={() => setEditConfirm(false)}
                   btnText="저장" secondBtnText="취소" hasTwoButtons
                   onOk={onEditMatch} onClickSecond={() => setEditConfirm(false)}>
        <div className="worldcup-settle-form">
          <p style={{textAlign: "center", marginBottom: 16}}>
            경기 정보 수정 (ID: {editTarget?.matchId})
          </p>
          {editHasBets && (
            <p style={{marginBottom: 12, fontSize: 14, color: "#c0392b"}}>
              예측이 존재하는 경기는 팀을 변경할 수 없습니다.
            </p>
          )}
          <div style={{marginBottom: 12}}>
            <span style={{marginRight: 8}}>홈팀</span>
            <Form.Select style={{maxWidth: 280, display: "inline-block"}}
                         value={editHomeTeamId}
                         disabled={editHasBets}
                         onChange={(e) => setEditHomeTeamId(e.target.value)}>
              <option value="">선택</option>
              {activeTeams.map((t) => (
                <option key={t.teamId} value={t.teamId}>{t.teamName} ({t.teamCode})</option>
              ))}
            </Form.Select>
          </div>
          <div style={{marginBottom: 12}}>
            <span style={{marginRight: 8}}>원정팀</span>
            <Form.Select style={{maxWidth: 280, display: "inline-block"}}
                         value={editAwayTeamId}
                         disabled={editHasBets}
                         onChange={(e) => setEditAwayTeamId(e.target.value)}>
              <option value="">선택</option>
              {activeTeams.map((t) => (
                <option key={t.teamId} value={t.teamId}>{t.teamName} ({t.teamCode})</option>
              ))}
            </Form.Select>
          </div>
          <div style={{marginBottom: 12}}>
            <span style={{marginRight: 8}}>경기 일시</span>
            <CustomDatePicker placeholder="킥오프" value={editKickoffAt} showTime
                              onChange={setEditKickoffAt}/>
          </div>
          <div>
            <span style={{marginRight: 8}}>정렬</span>
            <input type="number" value={editDisplayOrder}
                   onChange={(e) => setEditDisplayOrder(e.target.value)}/>
          </div>
          <p style={{marginTop: 12, fontSize: 14, color: "#666"}}>
            예측 마감은 경기 시작 {editTarget ? "이벤트 설정 기준" : ""} 자동 재계산됩니다.
          </p>
        </div>
      </ModalDialog>

      <ModalDialog show={cancelConfirm} onHide={() => setCancelConfirm(false)}
                   btnText="예" secondBtnText="아니요" hasTwoButtons
                   onOk={onCancel} onClickSecond={() => setCancelConfirm(false)}>
        <p style={{textAlign: "center"}}>
          경기를 취소하고 티켓을 환불하시겠습니까?<br/>
          ({cancelTarget?.homeTeamName} vs {cancelTarget?.awayTeamName})
        </p>
      </ModalDialog>

      <ModalDialog show={!!statsTarget} onHide={closeStats}
                   btnText="닫기" onOk={closeStats}>
        {statsData ? (
          <div className="worldcup-stats-modal">
            <p className="worldcup-stats-title">
              {statsTarget?.homeTeamName} vs {statsTarget?.awayTeamName}
            </p>
            <div className="worldcup-stats-grid">
              <div className="worldcup-stats-item">
                <span className="worldcup-stats-label">경기 ID</span>
                <b>{statsTarget?.matchId ?? "-"}</b>
              </div>
              <div className="worldcup-stats-item">
                <span className="worldcup-stats-label">정산 상태</span>
                <b>{statsData.match?.settlementStatus ?? "-"}</b>
              </div>
              <div className="worldcup-stats-item">
                <span className="worldcup-stats-label">누적 풀</span>
                <b>{Number(statsData.stats?.totalPoolPoint ?? 0).toLocaleString()}P</b>
              </div>
              <div className="worldcup-stats-item">
                <span className="worldcup-stats-label">예측 참여</span>
                <b>{Number(statsData.stats?.predictionCount ?? 0).toLocaleString()}건</b>
              </div>
              <div className="worldcup-stats-item">
                <span className="worldcup-stats-label">홈 티켓</span>
                <b>{Number(statsData.stats?.homeTicketSum ?? 0).toLocaleString()}장</b>
              </div>
              <div className="worldcup-stats-item">
                <span className="worldcup-stats-label">원정 티켓</span>
                <b>{Number(statsData.stats?.awayTicketSum ?? 0).toLocaleString()}장</b>
              </div>
              <div className="worldcup-stats-item">
                <span className="worldcup-stats-label">총 베팅 티켓</span>
                <b>{Number(statsData.stats?.totalTicketsBet ?? 0).toLocaleString()}장</b>
              </div>
              <div className="worldcup-stats-item">
                <span className="worldcup-stats-label">1장당 지급</span>
                <b>
                  {statsData.match?.payoutPerTicket != null
                    ? `${Number(statsData.match.payoutPerTicket).toLocaleString()}P`
                    : "-"}
                </b>
              </div>
            </div>
          </div>
        ) : (
          <p style={{textAlign: "center", margin: 0}}>통계를 불러오는 중입니다...</p>
        )}
      </ModalDialog>
    </article>
  );
}
